import React, {FC} from 'react';
import styled from 'styled-components';

import {TeamModel} from '../../../../../libs/shared/core/src/models/api/teamModel';
import {TableCell} from '../../../../../libs/shared/ui/src/components/tables/cells/tableCell';
import {
  TableCellProps,
  TableCellStyleProps,
} from '../../../../../libs/shared/ui/src/components/tables/cells/tableCellTypes';
import {
  getCellBackgroundColor,
  getCellIndexStyles,
} from '../../../../../libs/shared/ui/src/components/tables/helpers/tableCellHelpers';
import {getWidthSettingsStyles} from '../../../../../libs/shared/ui/src/components/tables/tableColumn';
import {fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {TeamPill} from '../../../../settings/common/settingsTeamPill';
import {OverflowWithCount} from '../../overflow/overflowWithCount';

/*
 * Consts.
 */

const teamCellPillContainerHorizontalPadding = 12;

/*
 * Props.
 */

interface TeamPillsCellProps extends TableCellProps<ReadonlyArray<TeamModel>> {}

/*
 * Style.
 */

const StyledTeamCellPillContainerDiv = styled.div<TableCellStyleProps>`
  padding: 0 ${teamCellPillContainerHorizontalPadding}px;
  display: flex;
  align-items: center;
  overflow: hidden;
  white-space: nowrap;
  height: 100%;
  ${(p) => getCellBackgroundColor(p.$isHovering, p.$isSelected)};
  ${(p) => getWidthSettingsStyles(p.$settings?.cellWidth, p.$index)};
  ${(p) =>
    getCellIndexStyles(
      p.$index,
      p.$isSelected,
      p.$shouldRemoveTopBorderRadius,
      p.$shouldRemoveBottomBorderRadius,
    )};
`;

const StyledTeamCellPillWrapperDiv = styled.div`
  width: 100%;
`;

const StyledPaddedDiv = styled.div`
  display: flex;
  flex-flow: row;
  width: inherit;
  padding-left: 0px;
  padding-right: 8px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: inherit;
`;

const StyledOverflowDiv = styled.div`
  font-weight: ${fontWeights.bold};
`;

/*
 * Component.
 */

export const TeamPillsCell: FC<TeamPillsCellProps> = (props) => {
  const {
    isSelected,
    isHovering,
    isLoading,
    size,
    settings,
    index,
    shouldShowStickyGradient,
    shouldRemoveTopBorderRadius,
    shouldRemoveBottomBorderRadius,
    value: teams,
  } = props;

  const renderSingleTeamPill = (team: TeamModel, teamIndex: number) => (
    <StyledPaddedDiv key={team.id}>
      <TeamPill team={team} />
      {teams && teamIndex < teams.length - 1 ? <div>,</div> : null}
    </StyledPaddedDiv>
  );

  return (
    <TableCell<ReadonlyArray<TeamModel>>
      isSelected={isSelected}
      isHovering={isHovering}
      isLoading={isLoading}
      size={size}
      settings={settings}
      index={index}
      value={teams}
      shouldShowStickyGradient={shouldShowStickyGradient}
      shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
      shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
      renderCell={(value) => (
        <StyledTeamCellPillContainerDiv
          $settings={settings}
          $isHovering={Boolean(isHovering)}
          $index={index}
          $isSelected={Boolean(isSelected)}
          $shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
          $shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
        >
          <StyledTeamCellPillWrapperDiv>
            <OverflowWithCount<TeamModel>
              elements={value}
              renderElement={renderSingleTeamPill}
              renderOverflowIndicator={(numberOfHiddenElements) => (
                <StyledOverflowDiv>+{numberOfHiddenElements}</StyledOverflowDiv>
              )}
            />
          </StyledTeamCellPillWrapperDiv>
        </StyledTeamCellPillContainerDiv>
      )}
    />
  );
};
